import React, { useState, useEffect } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  Line,
} from 'react-simple-maps';
import { messagesAPI } from '../services/api';
import axios from 'axios';
import './Map.css';

const geoUrl = 'https://unpkg.com/world-atlas@2/countries-110m.json';

// Real agents will be fetched from API and positioned at AWS regions

// Get color for each AWS region - brighter, more visible tones
const getRegionColor = (regionId) => {
  const regionColors = {
    "us-east-1": "#ff5555",      // Bright red
    "us-west-2": "#ff8855",      // Orange-red
    "eu-west-1": "#ff6666",      // Light red
    "eu-central-1": "#ff9966",   // Coral
    "ap-southeast-1": "#ff4444", // Vivid red
    "ap-northeast-1": "#ff5544", // Red-orange
    "ca-central-1": "#ff7766",   // Salmon
    "sa-east-1": "#ff6655",      // Bright coral
    "ap-south-1": "#ff8866",     // Light orange
    "eu-north-1": "#ff7788"      // Pink-red
  };
  return regionColors[regionId] || "#ff5555";
};

// Removed placeholder agents - using real agents from API

// Get region center coordinates for drawing transactions
const getRegionCenter = (regionId) => {
  const AWS_REGIONS = [
    { id: "us-east-1", coordinates: [-77.0369, 38.9072] },
    { id: "us-west-2", coordinates: [-122.6765, 45.5152] },
    { id: "eu-west-1", coordinates: [-6.2603, 53.3498] },
    { id: "eu-central-1", coordinates: [8.6821, 50.1109] },
    { id: "ap-southeast-1", coordinates: [103.8198, 1.3521] },
    { id: "ap-northeast-1", coordinates: [139.6917, 35.6895] },
    { id: "ca-central-1", coordinates: [-75.6919, 45.4215] },
    { id: "sa-east-1", coordinates: [-46.6333, -23.5505] },
    { id: "ap-south-1", coordinates: [72.8777, 19.0760] },
    { id: "eu-north-1", coordinates: [18.0686, 59.3293] }
  ];
  
  const region = AWS_REGIONS.find(r => r.id === regionId);
  return region ? region.coordinates : null;
};

const AgentMarker = ({ agent, isActive, onClick, onHover, onLeave }) => {
  const isOnline = agent.status === 'online';
  
  return (
    <Marker 
      coordinates={agent.coordinates} 
      onClick={() => onClick(agent)}
      onMouseEnter={(e) => onHover(agent, e)}
      onMouseMove={(e) => onHover(agent, e)}
      onMouseLeave={onLeave}
    >
      <g>
        {/* Outer glow ring - thin and subtle */}
        <circle
          r={isOnline ? (isActive ? 5 : 4) : (isActive ? 4 : 3)}
          fill="none"
          stroke={isOnline ? agent.color : "rgba(255, 255, 255, 0.2)"}
          strokeWidth={0.3}
          opacity={isOnline ? 0.4 : 0.2}
          className={isOnline ? "marker-pulse-online" : "marker-pulse"}
        />
        {/* Main dot */}
        <circle
          r={isOnline ? (isActive ? 2 : 1.6) : (isActive ? 1.8 : 1.3)}
          fill={agent.color}
          stroke="rgba(255, 255, 255, 0.4)"
          strokeWidth={0.25}
          className={isOnline ? "marker-glow-online" : "marker-glow"}
          style={{ cursor: 'pointer' }}
          opacity={0.85}
        />
        {/* Inner core */}
        <circle
          r={isOnline ? (isActive ? 0.7 : 0.5) : (isActive ? 0.6 : 0.4)}
          fill="rgba(255, 255, 255, 0.6)"
          opacity={isOnline ? 0.7 : 0.4}
        />
      </g>
    </Marker>
  );
};

const TransactionLine = ({ fromRegion, toRegion, transaction, isActive }) => {
  const fromCoords = getRegionCenter(fromRegion);
  const toCoords = getRegionCenter(toRegion);

  if (!fromCoords || !toCoords) return null;

  const lineColor = transaction.direction === 'response'
    ? 'rgba(255, 100, 100, 0.7)' : 'rgba(200, 150, 100, 0.7)';

  return (
    <>
      {/* Thin animated line - uses projected coordinates via Line */}
      <Line
        from={fromCoords}
        to={toCoords}
        stroke={lineColor}
        strokeWidth={isActive ? 1 : 0.5}
        strokeDasharray={isActive ? "6,3" : "none"}
        strokeLinecap="round"
        className={isActive ? "transaction-line-active" : "transaction-line"}
      />
    </>
  );
};

const Map = () => {
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [hoveredAgent, setHoveredAgent] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [activeTransactions, setActiveTransactions] = useState([]);
  const [showTransactionPanel, setShowTransactionPanel] = useState(false);
  const [realTransactions, setRealTransactions] = useState([]);
  const [realAgents, setRealAgents] = useState([]);
  const [agentStats, setAgentStats] = useState({
    total: 0,
    skill: 0,
    persona: 0,
    communication: 0,
    online: 0
  });
  const [lastUpdate, setLastUpdate] = useState(new Date()); // eslint-disable-line no-unused-vars
  const [regionStats, setRegionStats] = useState({});

  // Fetch real agent statistics
  useEffect(() => {
    fetchAgentStats();
    
    // Set up polling for real-time stats
    const interval = setInterval(fetchAgentStats, 60000); // Update every minute
    
    return () => clearInterval(interval);
  }, []);

  // Fetch real transactions
  useEffect(() => {
    fetchRealTransactions();
    
    // Set up polling for real-time transactions
    const interval = setInterval(fetchRealTransactions, 5000); // Update every 5 seconds
    
    return () => clearInterval(interval);
  }, []);

  // Update active transactions when real transactions change
  useEffect(() => {
    if (realTransactions.length > 0) {
      // Take the most recent 5 transactions for animation
      const recentTransactions = realTransactions
        .slice(0, 5)
        .map((tx, index) => ({
          ...tx,
          id: tx._id,
          animationDelay: index * 0.5, // Stagger animations
          direction: tx.type === 'a2a_response' ? 'response' : 'forward',
          startTime: Date.now() + (index * 500) // Track when animation starts
        }));
      
      setActiveTransactions(recentTransactions);
      
      // Clear transactions after animations complete (4s + stagger delay + buffer)
      const maxAnimationTime = 4000 + (recentTransactions.length * 500) + 500;
      setTimeout(() => {
        setActiveTransactions([]);
      }, maxAnimationTime);
      
    } else {
      // Clear transactions if no real ones are available
      setActiveTransactions([]);
    }
  }, [realTransactions]);

  const fetchRealTransactions = async () => {
    try {
      // Fetch recent messages with region data
      const response = await messagesAPI.getMessages({ limit: 20 });
      
      // Filter for transactions that have both from_region and to_region
      const transactions = response.filter(msg => 
        msg.from_region && 
        msg.to_region && 
        msg.from_region !== msg.to_region &&
        // Ensure these are real region IDs
        ['us-east-1', 'us-west-2', 'eu-west-1', 'eu-central-1', 
         'ap-southeast-1', 'ap-northeast-1', 'ca-central-1', 
         'sa-east-1', 'ap-south-1', 'eu-north-1'].includes(msg.from_region) &&
        ['us-east-1', 'us-west-2', 'eu-west-1', 'eu-central-1', 
         'ap-southeast-1', 'ap-northeast-1', 'ca-central-1', 
         'sa-east-1', 'ap-south-1', 'eu-north-1'].includes(msg.to_region)
      );
      
      console.log('Fetched real transactions:', transactions.length);
      setRealTransactions(transactions);
    } catch (error) {
      console.error('Failed to fetch real transactions:', error);
    }
  };

  const fetchAgentStats = async () => {
    try {
      const response = await axios.get('/api/agents?type=all&limit=10000');
      
      let agents;
      if (response.data.agents) {
        agents = response.data.agents;
      } else {
        agents = response.data;
      }

      // Map real agents to AWS region coordinates
      const AWS_REGIONS = [
        { id: "us-east-1", coordinates: [-77.0369, 38.9072] },
        { id: "us-west-2", coordinates: [-122.6765, 45.5152] },
        { id: "eu-west-1", coordinates: [-6.2603, 53.3498] },
        { id: "eu-central-1", coordinates: [8.6821, 50.1109] },
        { id: "ap-southeast-1", coordinates: [103.8198, 1.3521] },
        { id: "ap-northeast-1", coordinates: [139.6917, 35.6895] },
        { id: "ca-central-1", coordinates: [-75.6919, 45.4215] },
        { id: "sa-east-1", coordinates: [-46.6333, -23.5505] },
        { id: "ap-south-1", coordinates: [72.8777, 19.0760] },
        { id: "eu-north-1", coordinates: [18.0686, 59.3293] }
      ];

      // Distribute real agents across AWS regions
      // Group agents by region first to better distribute them
      const agentsByRegion = {};
      agents.forEach((agent, index) => {
        const regionIndex = index % AWS_REGIONS.length;
        const region = AWS_REGIONS[regionIndex];
        if (!agentsByRegion[region.id]) {
          agentsByRegion[region.id] = [];
        }
        agentsByRegion[region.id].push({ agent, region });
      });

      // Distribute agents in a tight grid around each region center
      const agentsWithCoordinates = [];
      Object.keys(agentsByRegion).forEach(regionId => {
        const regionAgents = agentsByRegion[regionId];
        const region = regionAgents[0].region;
        const count = regionAgents.length;

        // Grid layout with moderate spacing
        const cols = Math.ceil(Math.sqrt(count));
        const rows = Math.ceil(count / cols);
        const spacing = 1.0; // degrees between agents

        regionAgents.forEach((item, idx) => {
          const row = Math.floor(idx / cols);
          const col = idx % cols;

          // Center the grid on the region coordinate
          const lngOffset = (col - (cols - 1) / 2) * spacing;
          const latOffset = (row - (rows - 1) / 2) * spacing;

          const lng = region.coordinates[0] + lngOffset;
          const lat = region.coordinates[1] + latOffset;

          agentsWithCoordinates.push({
            ...item.agent,
            coordinates: [lng, lat],
            regionId: region.id,
            color: getRegionColor(region.id)
          });
        });
      });

      setRealAgents(agentsWithCoordinates);

      const skillCount = agents.filter(a => a.category === 'skill').length;
      const personaCount = agents.filter(a => a.category === 'persona').length;
      const commCount = agents.filter(a => a.category === 'communication').length;
      const onlineCount = agents.filter(a => a.status === 'online').length;

      // Calculate agents per region
      const regionCounts = {};
      AWS_REGIONS.forEach(region => {
        const count = agentsWithCoordinates.filter(a => a.regionId === region.id).length;
        const onlineInRegion = agentsWithCoordinates.filter(a => a.regionId === region.id && a.status === 'online').length;
        regionCounts[region.id] = { total: count, online: onlineInRegion };
      });
      setRegionStats(regionCounts);

      setAgentStats({
        total: agents.length,
        skill: skillCount,
        persona: personaCount,
        communication: commCount,
        online: onlineCount
      });
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to fetch agent stats for map:', error);
    }
  };

  const handleAgentClick = (agent) => {
    setSelectedAgent(agent);
    setShowTransactionPanel(true);
  };

  const handleAgentHover = (agent, event) => {
    setHoveredAgent(agent);
    if (event) {
      setTooltipPosition({ x: event.clientX, y: event.clientY });
    }
  };

  const handleAgentLeave = () => {
    setHoveredAgent(null);
  };

  // const handleTransactionClick = (transaction) => {
  //   setActiveTransaction(transaction);
  //   setShowTransactionPanel(true);
  // };

  // Get the first active transaction for the panel (if any)
  const displayTransaction = activeTransactions[0];

  return (
    <div className="map-page">
      <div className="map-header">
        <h1>NANDA Global Network</h1>
      </div>

      <div className="map-container">
          <ComposableMap
            projection="geoEqualEarth"
            projectionConfig={{ center: [0, -5], scale: 180 }}
            style={{ width: '100%', height: '100%', overflow: 'hidden' }}
          >
          {/* No separate ocean rect - CSS background handles it */}
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  style={{
                    default: {
                      fill: '#3a3a3a',
                      stroke: 'rgba(255, 255, 255, 0.15)',
                      strokeWidth: 0.4,
                      outline: 'none' 
                    },
                    hover: { 
                      fill: '#454545', 
                      outline: 'none' 
                    },
                    pressed: { 
                      fill: '#505050', 
                      outline: 'none' 
                    },
                  }}
                />
              ))
            }
          </Geographies>

          {/* Real agent markers */}
          {realAgents.map((agent, index) => (
            <AgentMarker
              key={agent.id}
              agent={agent}
              isActive={selectedAgent?.id === agent.id || 
                       activeTransactions.some(t => t.from === index || t.to === index)}
              onClick={handleAgentClick}
              onHover={handleAgentHover}
              onLeave={handleAgentLeave}
            />
          ))}

          {/* Active transaction lines */}
          {activeTransactions.map((transaction, index) => {
            // Skip if no region data
            if (!transaction.from_region || !transaction.to_region) {
              return null;
            }
            
            return (
              <TransactionLine
                key={`${transaction.id}-${index}`}
                fromRegion={transaction.from_region}
                toRegion={transaction.to_region}
                transaction={transaction}
                isActive={true}
              />
            );
          })}
        </ComposableMap>

        {/* Legend */}
        <div className="map-legend">
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
            <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#10b981', boxShadow: '0 0 6px #10b981' }}></div>
            <span style={{ fontSize: '0.6875rem', color: '#e8e8e8', fontWeight: '400' }}>
              {agentStats.total} agents &middot; {Object.keys(regionStats).filter(k => regionStats[k]?.total > 0).length} regions
            </span>
          </div>
          <div className="legend-items">
            <div className="legend-item">
              <div className="legend-marker" style={{ backgroundColor: '#ffaa64' }}></div>
              <span>Request</span>
            </div>
            <div className="legend-item">
              <div className="legend-marker" style={{ backgroundColor: '#ff6464' }}></div>
              <span>Response</span>
            </div>
          </div>
          <div style={{ marginTop: '10px', fontSize: '0.5625rem', color: 'rgba(255,255,255,0.35)' }}>
            {activeTransactions.length > 0 ? `${activeTransactions.length} live transactions` : 'Listening for activity\u2026'}
          </div>
        </div>
      </div>

      {/* Hover Tooltip */}
      {hoveredAgent && (
        <div 
          className="agent-tooltip"
          style={{
            position: 'fixed',
            left: `${tooltipPosition.x + 15}px`,
            top: `${tooltipPosition.y + 15}px`,
            background: 'rgba(20, 20, 20, 0.95)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '8px',
            padding: '12px 16px',
            pointerEvents: 'none',
            zIndex: 1000,
            minWidth: '200px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <div style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px' }}>
            {hoveredAgent.name}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>
            <span style={{ color: hoveredAgent.status === 'online' ? 'var(--success)' : 'var(--error)' }}>●</span>
            {' '}{hoveredAgent.status === 'online' ? 'Online' : 'Offline'}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>
            Type: {hoveredAgent.category || 'N/A'}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
            Region: {hoveredAgent.regionId}
          </div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
            Click for details
          </div>
        </div>
      )}

      {/* Transaction Panel */}
      {showTransactionPanel && (
        <div className="transaction-panel">
          <div className="panel-header">
            <div className="panel-title">
              {selectedAgent ? `Agent: ${selectedAgent.name}` : 'Live Transaction'}
            </div>
            <button 
              className="panel-close"
              onClick={() => {
                setShowTransactionPanel(false);
                setSelectedAgent(null);
              }}
            >
              ×
            </button>
          </div>
          
          <div className="panel-content">
            {selectedAgent ? (
              <div className="agent-details">
                <div className="detail-row">
                  <span className="detail-label">Agent:</span>
                  <span className="detail-value">{selectedAgent.name}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">AWS Region:</span>
                  <span className="detail-value">{selectedAgent.region} ({selectedAgent.regionName})</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Specialty:</span>
                  <span className="detail-value">{selectedAgent.specialty}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Type:</span>
                  <span className="detail-value">{selectedAgent.type}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Status:</span>
                  <span className="detail-value status-online">Online</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Coordinates:</span>
                  <span className="detail-value">
                    {selectedAgent.coordinates[1].toFixed(2)}°, {selectedAgent.coordinates[0].toFixed(2)}°
                  </span>
                </div>
              </div>
            ) : (
              <div className="transaction-details">
                <div className="detail-row">
                  <span className="detail-label">From Agent:</span>
                  <span className="detail-value">{displayTransaction?.from_agent || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">To Agent:</span>
                  <span className="detail-value">{displayTransaction?.to_agent || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">From Region:</span>
                  <span className="detail-value">{displayTransaction?.from_region || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">To Region:</span>
                  <span className="detail-value">{displayTransaction?.to_region || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Type:</span>
                  <span className="detail-value">{displayTransaction?.type || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Timestamp:</span>
                  <span className="detail-value">
                    {displayTransaction?.timestamp ? new Date(displayTransaction.timestamp).toLocaleTimeString() : 'N/A'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Active Transactions:</span>
                  <span className="detail-value">{activeTransactions.length}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Map;
